import React, { useEffect, useRef, useState } from "react";
import basicSavingsIllustration from "../../images/basic-savings-illustration.svg";
import targetSavingsIllustration from "../../images/target-savings-illustration.svg";
import fixedDepositIllustration from "../../images/fixed-deposit-illustration.svg";

const raleway = { fontFamily: "'Geist', sans-serif" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };

// Matches the fixed Header height in src/components/header.jsx
const HEADER_HEIGHT = 88;
// Sensible default for the combined title+progress sticky header before measurement.
const STICKY_HEADER_DEFAULT_HEIGHT = 280;
const PROGRESS_GAP_PX = 24;

const OPTIONS = [
  {
    title: "Basic Savings",
    description:
      "For everyday saving with easy access to your money whenever you need it.",
    rate: "4%",
    rateNote: "Interest paid monthly",
    cardBg: "#E5EDE3",
    illustration: basicSavingsIllustration,
  },
  {
    title: "Target Savings",
    description:
      "Save gradually toward a specific goal like rent, school fees, travel, or business plans.",
    rate: "6%",
    rateNote: "Auto-debit from any account",
    cardBg: "#0B2B1B",
    illustration: targetSavingsIllustration,
  },
  {
    title: "Fixed Deposit",
    description:
      "Lock in funds for a set period and earn higher returns over time.",
    rate: "18%",
    rateNote: "90, 180 or 365-day terms",
    cardBg: "#E5EDE3",
    illustration: fixedDepositIllustration,
  },
];

const Arrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 7H11M11 7L7.5 3.5M11 7L7.5 10.5"
      stroke="#132A22"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OpenAccountButton = () => (
  <a
    href="https://zikoramfb.qoreonline.com/onboarding/type"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      ...raleway,
      position: "absolute",
      right: "clamp(16px, 1.94vw, 28px)",
      bottom: "clamp(16px, 1.94vw, 28px)",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      height: "40px",
      padding: "0 16px",
      background: "#7DBE9C",
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "14px",
      color: "#132A22",
      textDecoration: "none",
      zIndex: 2,
    }}
  >
    Open Account <Arrow />
  </a>
);

const SavingsOptions = () => {
  const sectionRefs = useRef([]);
  const stickyHeaderRef = useRef(null);
  const sliderRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(
    STICKY_HEADER_DEFAULT_HEIGHT
  );

  const stickyOffset = HEADER_HEIGHT + stickyHeaderHeight;

  useEffect(() => {
    const measure = () => {
      const h = stickyHeaderRef.current?.offsetHeight;
      if (h && h > 0) setStickyHeaderHeight(h);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    // Skip the sticky-scroll behavior on small screens where the layout
    // collapses to a normal vertical flow.
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 900px)");
    if (mql.matches) return;

    let frame = 0;

    const update = () => {
      const refs = sectionRefs.current;
      if (!refs?.length || !sliderRef.current) return;

      const offsetTop =
        HEADER_HEIGHT +
        (stickyHeaderRef.current?.offsetHeight || stickyHeaderHeight);
      const transitionDistance = window.innerHeight * 0.45;

      let progress = 0;
      for (let i = 0; i < refs.length; i++) {
        const ref = refs[i];
        if (!ref) continue;
        const rect = ref.getBoundingClientRect();

        // A card is "reached" once its top has met the sticky offset.
        if (rect.top - offsetTop <= 1) {
          progress = i;
          // Smoothly interpolate toward the next card as it approaches.
          const nextRef = refs[i + 1];
          if (nextRef) {
            const nextRect = nextRef.getBoundingClientRect();
            const distance = nextRect.top - offsetTop;
            if (distance <= 0) {
              progress = i + 1;
            } else if (distance < transitionDistance) {
              progress = i + (1 - distance / transitionDistance);
            }
          }
        }
      }

      const clamped = Math.max(0, Math.min(refs.length - 1, progress));
      const totalGap = PROGRESS_GAP_PX * (refs.length - 1);
      sliderRef.current.style.left = `calc(${clamped} * ((100% - ${totalGap}px) / ${refs.length} + ${PROGRESS_GAP_PX}px))`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [stickyHeaderHeight]);

  return (
    <>
      <style>{`
				@media (max-width: 900px) {
					.savings-stack-card {
						position: relative !important;
						top: auto !important;
						min-height: 0 !important;
						padding-top: clamp(40px, 8vw, 60px) !important;
						padding-bottom: clamp(40px, 8vw, 60px) !important;
					}
					.savings-sticky-header {
						position: static !important;
					}
					.savings-sticky-header-inner {
						padding-bottom: clamp(24px, 4vw, 40px) !important;
					}
					.savings-option-row { flex-direction: column !important; gap: 24px !important; align-items: stretch !important; }
					.savings-option-text { width: 100% !important; gap: 20px !important; }
					.savings-option-card { width: 100% !important; min-height: 240px !important; }
					.savings-option-illustration { max-width: 75% !important; max-height: 60% !important; }
				}
				@media (max-width: 480px) {
					.savings-option-card { min-height: 200px !important; }
					.savings-option-illustration { max-width: 80% !important; max-height: 55% !important; }
				}
			`}</style>

      <section
        id="savings-options"
        style={{
          width: "100%",
          backgroundColor: "#FFFFFF",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        {/* Sticky header — heading + progress bar stay pinned together
				    below the fixed page Header throughout the scroll animation. */}
        <div
          ref={stickyHeaderRef}
          className="savings-sticky-header"
          style={{
            position: "sticky",
            top: `${HEADER_HEIGHT}px`,
            zIndex: 50,
            background: "#FFFFFF",
          }}
        >
          <div
            className="savings-sticky-header-inner"
            style={{
              padding:
                "clamp(32px, 4.5vw, 64px) clamp(20px, 5.56vw, 80px) clamp(16px, 2vw, 24px)",
            }}
          >
            <div
              style={{
                maxWidth: "1280px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "clamp(10px, 1.1vw, 16px)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#295B4A",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    ...geistMono,
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: 1,
                    letterSpacing: "0.1em",
                    color: "#295B4A",
                    textTransform: "uppercase",
                  }}
                >
                  Saving Accounts
                </span>
              </div>

              <h2
                style={{
                  ...instrumentSerif,
                  fontWeight: 400,
                  fontSize: "clamp(28px, 3.6vw, 52px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  color: "#132A22",
                  margin: 0,
                  maxWidth: "min(720px, 100%)",
                }}
              >
                Savings options built for different goals
              </h2>

              <p
                style={{
                  ...raleway,
                  fontWeight: 400,
                  fontSize: "clamp(13px, 1vw, 15px)",
                  lineHeight: 1.6,
                  color: "#3F5445",
                  margin: 0,
                  maxWidth: "min(560px, 100%)",
                }}
              >
                Choose an account that matches how you want to save, grow, or
                plan ahead.
              </p>

              {/* Progress bar lines + sliding active indicator */}
              <div
                style={{
                  width: "100%",
                  maxWidth: "1280px",
                  marginTop: "clamp(16px, 2vw, 28px)",
                  position: "relative",
                  display: "flex",
                  gap: `${PROGRESS_GAP_PX}px`,
                  alignItems: "center",
                  height: "3px",
                }}
              >
                {OPTIONS?.map((opt) => (
                  <div
                    key={opt?.title}
                    style={{
                      flex: 1,
                      height: "1px",
                      background: "#E1E5E2",
                    }}
                  />
                ))}
                <div
                  ref={sliderRef}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    height: "3px",
                    width: `calc((100% - ${
                      PROGRESS_GAP_PX * (OPTIONS.length - 1)
                    }px) / ${OPTIONS.length})`,
                    background: "#143628",
                    borderRadius: "2px",
                    willChange: "left",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky stack — each card pins below the progress bar and is
				    covered by the next card as the user scrolls. */}
        <div>
          {OPTIONS?.map((opt, idx) => {
            const textColor = "#132A22";
            const subColor = "#3F5445";
            const rateColor = "#295B4A";

            return (
              <div
                key={opt?.title}
                ref={(el) => (sectionRefs.current[idx] = el)}
                className="savings-stack-card"
                style={{
                  position: "sticky",
                  top: `${stickyOffset}px`,
                  zIndex: idx + 1,
                  background: "#FFFFFF",
                  padding:
                    "clamp(20px, 2.5vw, 36px) clamp(20px, 5.56vw, 80px) clamp(28px, 3.5vw, 56px)",
                  boxSizing: "border-box",
                  minHeight: `calc(100vh - ${stickyOffset}px)`,
                }}
              >
                <div
                  className="savings-option-row"
                  style={{
                    maxWidth: "1280px",
                    margin: "0 auto",
                    display: "flex",
                    gap: "clamp(24px, 4.17vw, 60px)",
                    alignItems: "center",
                    minHeight: `calc(100vh - ${stickyOffset}px - clamp(48px, 6vw, 92px))`,
                  }}
                >
                  {/* Left text column */}
                  <div
                    className="savings-option-text"
                    style={{
                      width: "38%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "clamp(20px, 2.5vw, 36px)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <h3
                        style={{
                          ...instrumentSerif,
                          fontWeight: 400,
                          fontSize: "clamp(24px, 2.5vw, 36px)",
                          lineHeight: 1.1,
                          letterSpacing: "-0.01em",
                          color: textColor,
                          margin: 0,
                        }}
                      >
                        {opt?.title}
                      </h3>
                      <p
                        style={{
                          ...raleway,
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: 1.6,
                          color: subColor,
                          margin: 0,
                          maxWidth: "300px",
                        }}
                      >
                        {opt?.description}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          ...instrumentSerif,
                          fontWeight: 400,
                          fontSize: "clamp(40px, 4.17vw, 60px)",
                          lineHeight: 1,
                          color: rateColor,
                        }}
                      >
                        {opt?.rate}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          paddingBottom: "6px",
                        }}
                      >
                        <span
                          style={{
                            ...geistMono,
                            fontWeight: 500,
                            fontSize: "11px",
                            letterSpacing: "0.1em",
                            color: "#5E5E5E",
                            textTransform: "uppercase",
                          }}
                        >
                          Per Annum
                        </span>
                        <span
                          style={{
                            ...raleway,
                            fontWeight: 500,
                            fontSize: "13px",
                            color: textColor,
                          }}
                        >
                          {opt?.rateNote}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right illustration card */}
                  <div
                    className="savings-option-card"
                    style={{
                      flex: 1,
                      position: "relative",
                      background: opt?.cardBg,
                      borderRadius: "20px",
                      minHeight: "clamp(260px, 26vw, 360px)",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "clamp(24px, 3vw, 48px)",
                    }}
                  >
                    <img
                      className="savings-option-illustration"
                      src={opt?.illustration}
                      alt={`${opt?.title} illustration`}
                      style={{
                        maxWidth: "85%",
                        maxHeight: "75%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                    <OpenAccountButton />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default SavingsOptions;
